import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    LikeBar,
    ReactionBar,
    Bookmark,
    PresenceBadge,
    Discussion,
} from "../components";

/* ----------------- helpers ----------------- */
const weaponIcon = (w) => {
    const cat = (w.category || w.type || "").toLowerCase();
    const tags = (w.tags || []).map(t => t.toLowerCase());

    if (cat.includes("pistol")) return "🔫";
    if (cat.includes("smg")) return "💥";
    if (cat.includes("assault")) return "🧨";
    if (cat.includes("shotgun")) return "🔥";
    if (cat.includes("sniper")) return "🎯";
    if (cat.includes("melee")) return "🗡️";
    if (cat.includes("throw")) return "💣";
    if (cat.includes("heavy")) return "🚀";
    if (cat.includes("special") || tags.includes("utility")) return "⚡";

    return "❓";
};

/* ----------------- section ----------------- */
function Section({ id, title, children }) {
    const headingId = title ? `${id}-h` : undefined;

    return (
        <section
            id={id}
            className="vw-sec"
            {...(title
                ? { "aria-labelledby": headingId }
                : { "aria-label": id })}
        >
            {title && (
                <h2 className="vw-h2" id={headingId}>
                    {title}
                </h2>
            )}
            {children}
        </section>
    );
}

/* ----------------- main ----------------- */
export default function WeaponDetailContent({
    w,
    entityId,
    basePath,
    bookmarkKey,
    selectedReactionFromBar,
    setSelectedReactionFromBar,
}) {
    if (!w) return null;

    return (
        <div className="vw-panel-body">
            {/* HERO */}
            <header className="vw-hero-row">
                <div className="vw-hero-left">
                    <div className="vw-portrait-wrap">
                        {w.portrait || w.image ? (
                            <img
                                className="vw-portrait"
                                src={w.portrait || w.image}
                                alt={`${w.name} image`}
                                loading="lazy"
                            />
                        ) : (
                            <div
                                className="vw-portrait vw-portrait--placeholder"
                                aria-label="No image"
                            >
                                {weaponIcon(w)}
                            </div>
                        )}
                    </div>
                </div>

                <div className="vw-hero-right">
                    {/* CHIPS */}
                    <div className="vw-chips">
                        {w.category && <span className="vw-chip">🧩 {w.category}</span>}
                        {w.manufacturer && <span className="vw-chip">🏭 {w.manufacturer}</span>}
                        {typeof w.price === "number" && (
                            <span className="vw-chip">
                                💵 ${Number(w.price).toLocaleString("en-US")}
                            </span>
                        )}
                        {w.ammoType && <span className="vw-chip">🔋 {w.ammoType}</span>}
                        {w.gameMode && <span className="vw-chip">🎮 {w.gameMode}</span>}
                    </div>

                    <h1 className="vw-title">{w.name}</h1>

                    {/* Selected reaction preview */}
                    {selectedReactionFromBar && (
                        <div className="vw-featured-reaction" aria-hidden>
                            {{
                                love: "❤️ Love",
                                fire: "🔥 Hot",
                                funny: "😂 Funny",
                                cool: "🆒 Cool",
                                helpful: "💡 Helpful",
                                applause: "👏 Applause",
                                curious: "❓ Curious",
                                bug: "🐞 Issue",
                            }[selectedReactionFromBar] || selectedReactionFromBar}
                        </div>
                    )}

                    {/* DESCRIPTION */}
                    {w.description?.trim?.() && (
                        <Section id="desc">
                            <div className="vw-md">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {w.description}
                                </ReactMarkdown>
                            </div>
                        </Section>
                    )}
                </div>
            </header>

            {/* QUICK STATS */}
            {(w.damage || w.fireRate || w.accuracy || w.range) && (
                <section className="vw-panel-section">
                    <div className="vw-facts-grid">
                        {w.damage != null && (
                            <div className="vw-fact">
                                <span>Damage</span>
                                <strong>{w.damage}</strong>
                            </div>
                        )}
                        {w.fireRate != null && (
                            <div className="vw-fact">
                                <span>Fire rate</span>
                                <strong>{w.fireRate}</strong>
                            </div>
                        )}
                        {w.accuracy != null && (
                            <div className="vw-fact">
                                <span>Accuracy</span>
                                <strong>{w.accuracy}</strong>
                            </div>
                        )}
                        {w.range != null && (
                            <div className="vw-fact">
                                <span>Range</span>
                                <strong>{w.range}</strong>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* MAIN CONTENT */}
            <div className="vw-grid">
                <main className="vw-main">
                    {w.acquisition?.trim?.() && (
                        <Section id="acq" title="How to Get">
                            <div className="vw-md">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {w.acquisition}
                                </ReactMarkdown>
                            </div>
                        </Section>
                    )}

                    {Array.isArray(w.attachments) && w.attachments.length > 0 && (
                        <Section id="attachments" title="Attachments">
                            <ul className="vw-list">
                                {w.attachments.map((a, i) => (
                                    <li key={i}>{a}</li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {Array.isArray(w.gallery) && w.gallery.length > 0 && (
                        <Section id="gallery" title="Gallery">
                            <div className="vw-gallery-grid">
                                {w.gallery.map((src, i) => (
                                    <img
                                        key={i}
                                        className="vw-shot"
                                        src={src}
                                        alt={`Image ${i + 1}`}
                                        loading="lazy"
                                    />
                                ))}
                            </div>
                        </Section>
                    )}
                </main>
            </div>

            {/* ENGAGEMENT */}
            <div className="vw-engage vw-engage--panel">
                <PresenceBadge presenceKey="weapons" entityId={entityId} />
                <LikeBar basePath={basePath} />
                <ReactionBar
                    basePath={basePath}
                    visibleReactions={[
                        "love",
                        "fire",
                        "funny",
                        "cool",
                        "helpful",
                        "applause",
                        "bug",
                    ]}
                    onSelect={(rid) => setSelectedReactionFromBar?.(rid)}
                />
                <Bookmark bookmarkKey={bookmarkKey} />
            </div>

            {/* DISCUSSION */}
            <div className="vw-discussion-panel">
                <Discussion basePath={basePath} />
            </div>
        </div>
    );
}
